/** @jsx jsx */
import React, { useEffect, useState, useRef, useLayoutEffect, useCallback, useMemo } from 'react';

import { css, jsx } from '@compiled/react';
import { N0, N200, N20, N30, N90 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
// TODO HL-1430 move these utils into our package once JWM business page is replaced with ours
import { useScrollToEnd } from '@atlassian/jira-business-scroll-to-end/src/index.tsx';
import { Link } from '@atlassian/react-resource-router';
import {
	useSummaryFeatures,
	type LinksResolver,
	type LinksTarget,
} from '../../../controllers/features-context/index.tsx';
import type { PaginatedDataProps } from '../../../ui/avp-bridge/with-paginated-data/index.tsx';
import { ScrollingLoader } from '../scrolling-loader/index.tsx';
import { useUFOCustomHoverData } from '../ufo-custom-data-toggle/index.tsx';
import {
	ColorSchemes,
	MetricTypes,
	type ColorScheme,
	type CountMetric,
	type DistributionMetric,
	type MetricType,
} from '../../types.tsx';

const BAR_HEIGHT = 24;
const BAR_BORDER_RADIUS = 2;

export type SinglePercentageTableRenderRowArgs<T> = {
	item: T;
	totalUnresolvedIssues: number;
	counterText: string;
	linksResolver: LinksResolver;
	linksTarget: LinksTarget;
	count: number;
	colorScheme?: ColorScheme;
} & MetricDiscriminatedUnion;

type MetricDiscriminatedUnion = { metric?: CountMetric | DistributionMetric };

type SinglePercentageTableProps<T> = PaginatedDataProps<T> & {
	typeText: string;
	distributionText: string;
	counterText: string;
	RowComponent: React.ComponentType<SinglePercentageTableRenderRowArgs<T>>;
	getKey: (item: T) => React.Key;
	metricType: MetricType;
	countSelector: (item: T) => number; // This is a function that returns the count for the item for the row component
	colorScheme?: ColorScheme;
} & MetricDiscriminatedUnion;

export const SinglePercentageTable = <T,>({
	data,
	typeText,
	distributionText,
	counterText,
	fetchNext,
	loadingNext,
	hasNextPage,
	metaData: { totalUnresolvedIssues = 0 },
	RowComponent,
	getKey,
	metricType = MetricTypes.DISTRIBUTION,
	countSelector,
	colorScheme,
}: SinglePercentageTableProps<T>) => {
	const { onScroll } = useScrollToEnd({
		onScrollToEnd: fetchNext,
		isDisabled: loadingNext || !hasNextPage,
		options: {
			onEndReachedThreshold: 150,
		},
	});

	const { linksResolver, linksTarget } = useSummaryFeatures();

	const metric = useMemo(
		() =>
			metricType === MetricTypes.COUNT
				? {
						metricType: MetricTypes.COUNT,
						maxCount: data.reduce((acc, item) => Math.max(acc, countSelector(item)), 0),
					}
				: {
						metricType: MetricTypes.DISTRIBUTION,
						maxCount: undefined,
					},
		[metricType, data, countSelector],
	);

	const rows = useMemo(
		() =>
			data.map((item) => (
				<RowComponent
					key={getKey(item)}
					count={countSelector(item)}
					item={item}
					totalUnresolvedIssues={totalUnresolvedIssues}
					counterText={counterText}
					linksResolver={linksResolver}
					linksTarget={linksTarget}
					metric={metric}
					colorScheme={colorScheme}
				/>
			)),
		[
			countSelector,
			counterText,
			data,
			linksTarget,
			totalUnresolvedIssues,
			getKey,
			linksResolver,
			RowComponent,
			metric,
			colorScheme,
		],
	);

	return (
		<div onScroll={onScroll} css={tableContainerStyles}>
			<table css={tableStyles}>
				<thead css={tableHeaderStyles}>
					<tr>
						<th>
							<h6 css={columnTitleStyles}>{typeText}</h6>
						</th>
						<th>
							<h6 css={columnTitleStyles}>{distributionText}</h6>
						</th>
					</tr>
				</thead>
				<tbody css={tableBodyStyles}>{rows}</tbody>
			</table>
			{loadingNext && <ScrollingLoader />}
		</div>
	);
};

type PercentageTableRowProps = {
	children: React.ReactNode;
	count: number;
	totalUnresolvedIssues: number;
	counterText: string;
	onClickHref: string;
	linksTarget: LinksTarget;
	onClick: () => void;
	linkAriaLabel?: string;
	colorScheme?: ColorScheme;
} & MetricDiscriminatedUnion;

export const PercentageTableRow = ({
	children,
	count,
	totalUnresolvedIssues,
	counterText,
	onClickHref,
	linksTarget,
	onClick,
	linkAriaLabel,
	metric,
	colorScheme,
}: PercentageTableRowProps) => (
	<tr css={tableRowStyles}>
		<td css={tableTypeColumnStyles}>
			<div css={typeContainerStyles}>{children}</div>
		</td>
		<td css={tablePercentageColumnStyles}>
			<Link
				href={onClickHref}
				css={percentageBarLinkStyles}
				target={linksTarget}
				onClick={onClick}
				aria-label={linkAriaLabel}
			>
				<PercentageBar
					totalUnresolvedIssues={totalUnresolvedIssues}
					count={count}
					counterText={counterText}
					metric={metric}
					colorScheme={colorScheme}
				/>
			</Link>
		</td>
	</tr>
);

const useMovingTooltip = (isTooltipVisible: boolean) => {
	const updateTooltip = useRef<() => void>();
	const [mouseMoved, setMouseMoved] = useState(false);

	const handleMouseMove = useCallback(() => {
		setMouseMoved((prev) => !prev);
	}, []);

	useEffect(() => {
		if (isTooltipVisible) {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window.addEventListener('mousemove', handleMouseMove);
		}
		return () => {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window.removeEventListener('mousemove', handleMouseMove);
		};
	}, [isTooltipVisible, handleMouseMove]);

	useLayoutEffect(() => {
		if (isTooltipVisible) {
			updateTooltip.current?.();
		}
	}, [mouseMoved, isTooltipVisible]);

	return updateTooltip;
};

const PercentageBar = ({
	totalUnresolvedIssues,
	count,
	counterText,
	metric,
	colorScheme = ColorSchemes.DEFAULT,
}: {
	totalUnresolvedIssues: number;
	count: number;
	counterText: string;
	colorScheme?: ColorScheme;
} & MetricDiscriminatedUnion) => {
	const [isTooltipVisible, setIsTooltipVisible] = useState(false);
	const { addUFOCustomHoverData, UFOConditionalCustomHoverData } = useUFOCustomHoverData();

	let percentage = 0;
	let barText = '';
	let width: string | number = 0;

	if (metric?.metricType === MetricTypes.COUNT) {
		barText = `${count}`;
		width = `${Math.round((count / metric.maxCount) * 100)}%`;
	} else {
		percentage = Math.round((count / totalUnresolvedIssues) * 100);
		barText = `${percentage}%`;
		width = percentage > 0 ? `${percentage}%` : 0;
	}

	const updateTooltip = useMovingTooltip(isTooltipVisible);

	const showPercentage = totalUnresolvedIssues !== 0;

	return (
		<>
			<Tooltip
				content={({ update }) => {
					updateTooltip.current = update;
					return `${metric?.metricType === MetricTypes.COUNT ? '' : `${percentage}% `} (${count}/${totalUnresolvedIssues} ${counterText})`;
				}}
				position="mouse"
				mousePosition="bottom-start"
				delay={0}
				onShow={() => {
					setIsTooltipVisible(true);
					addUFOCustomHoverData();
				}}
				onHide={() => setIsTooltipVisible(false)}
			>
				{(tooltipProps) => (
					<div css={percentageContainerStyles} {...(showPercentage ? tooltipProps : {})}>
						<div css={percentageBarStyles}>
							<div
								css={[
									percentageBarFillStyles,
									colorScheme === ColorSchemes.BLUE && percentageBarBlueFillStyles,
								]}
								// eslint-disable-next-line jira/react/no-style-attribute
								style={{
									width,
									borderRadius: percentage === 100 ? `${BAR_BORDER_RADIUS}px` : undefined,
								}}
							>
								{showPercentage && (
									<>
										{/* eslint-disable-next-line jira/i18n/text-wrapped-in-format-message */}
										<p css={[baseLabelTextStyles, truncatedTextStyles]}>...</p>
										<p css={[baseLabelTextStyles, percentageTextStyles]}>{barText}</p>
									</>
								)}
							</div>
						</div>
					</div>
				)}
			</Tooltip>
			<UFOConditionalCustomHoverData />
		</>
	);
};

const tableContainerStyles = css({
	overflowY: 'auto',
	marginRight: token('space.negative.150', '-12px'),
	paddingRight: token('space.150', '12px'),
});

const tableStyles = css({
	margin: 0,
	marginBottom: token('space.050', '4px'),
});

const tableHeaderStyles = css({
	borderBottom: 0,
});

const columnTitleStyles = css({
	color: token('color.text.subtlest', N200),
	textTransform: 'none',
});

const tableBodyStyles = css({
	borderBottom: 0,
});

const tableRowStyles = css({
	'&:hover': {
		backgroundColor: token('elevation.surface.hovered', N20),
	},
});

const tableTypeColumnStyles = css({
	width: '30%',
	padding: '0',
	paddingLeft: token('space.050', '4px'),
	paddingRight: token('space.100', '8px'),
});

const tablePercentageColumnStyles = css({
	padding: '0',
});

const percentageBarLinkStyles = css({
	'&:hover': {
		textDecoration: 'none',
	},
	'&:focus': {
		outline: 'auto',
	},
});

const typeContainerStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.100', '8px'),
	marginLeft: token('space.050', '4px'),
});

const percentageContainerStyles = css({
	display: 'flex',
	alignItems: 'center',
	paddingTop: token('space.100', '8px'),
	paddingRight: 0,
	paddingBottom: token('space.100', '8px'),
	paddingLeft: 0,
});

const percentageBarStyles = css({
	display: 'flex',
	flex: 'auto',
	height: `${BAR_HEIGHT}px`,
	borderRadius: `${BAR_BORDER_RADIUS}px`,
	backgroundColor: token('color.background.accent.gray.subtler', N30),
	position: 'relative',
});

const percentageBarFillStyles = css({
	backgroundColor: token('color.chart.neutral', N90),
	borderRadius: `${BAR_BORDER_RADIUS}px 0 0 ${BAR_BORDER_RADIUS}px`,
	containerType: 'inline-size',
	containerName: 'percentageContainer',
});

const percentageBarBlueFillStyles = css({
	backgroundColor: token('color.chart.brand', '#1D7AFC'),
});

const baseLabelTextStyles = css({
	color: token('color.text.inverse', N0),
	position: 'absolute',
	left: '12px',
	top: '50%',
	transform: 'translateY(-50%)',
	pointerEvents: 'none',
	zIndex: 1,
	margin: 0,
});

const truncatedTextStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-container-queries
	'@container percentageContainer (width < 34px) or (width >= 42px)': {
		display: 'none',
	},
});

const percentageTextStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-container-queries
	'@container percentageContainer (width < 42px)': {
		display: 'none',
	},
});
